import { ComponentStyleConfig } from '@chakra-ui/react';
import { getColor } from '@chakra-ui/theme-tools';
import { CSSProperties } from 'react';

/**
 * Unknown color tokens, need to add to base theme?.
 */
const warningColor0 = '#AB5C00';
const disabledColor0 = '#ADB9C7';

/**
 * Extends base Select theme with 3p Figma design.
 *
 * @see https://github.com/chakra-ui/chakra-ui/blob/092b20f3e93064c1db312062b76b420a7ed1703b/packages/theme/src/components/input.ts
 * @see https://www.figma.com/file/AVVZO09XE8fZ0uuOaJYcQg/B2C-Onboarding---for-Dev?node-id=2705%3A56301
 */
export const SelectControl: ComponentStyleConfig = {
    parts: [
        'Select',
        'SelectIcon',
        'FormControl',
        'FormLabel',
        'FormHelperText',
        'FormErrorMessage',
        'PlaceHolderText'
    ],
    baseStyle: ({
        isFocused,
        isInvalid,
        isDisabled,
        isWarning,
        isSuccess,
        isUserWarning,
        theme
    }) => {
        const isLabelOnTop = true;
        const formLabelIsActiveColor = isFocused;
        // Manually get colors for theming boxShadow
        const warning500 = getColor(theme, 'warning.500');
        const secondary400 = getColor(theme, 'secondary.400');
        const success500 = getColor(theme, 'success.500');
        const neutral300 = getColor(theme, 'neutral.300');
        const neutral600 = getColor(theme, 'neutral.600');
        const error700 = getColor(theme, 'error.700');
        const primary500 = getColor(theme, 'primary.500');
        const formLabelColor = (): CSSProperties['color'] => {
            if ((isWarning || isUserWarning) && !isFocused && isLabelOnTop) {
                return warningColor0;
            }
            if (isSuccess && !isFocused && isLabelOnTop) {
                return 'success.700';
            }
            if (isInvalid) {
                return isLabelOnTop ? 'error.700' : 'neutral.600';
            }

            return formLabelIsActiveColor ? 'primary.700' : 'neutral.600';
        };
        const helperTextColor = (): CSSProperties['color'] => {
            if ((isWarning || isUserWarning) && !isFocused) {
                return warningColor0;
            }
            if (isSuccess && !isFocused) {
                return 'success.700';
            }

            return isDisabled ? disabledColor0 : 'neutral.600';
        };
        const inputBorderColor = (): CSSProperties['boxShadow'] => {
            if (isWarning && !isFocused) {
                return `inset 0 0 0 1px ${warning500}`;
            }

            if (isUserWarning && !isFocused) {
                return `inset 0 0 0 1px ${secondary400}`;
            }

            if (isSuccess && !isFocused) {
                return `inset 0 0 0 1px ${success500}`;
            }

            return `inset 0 0 0 1px ${neutral300}`;
        };
        const inputHoverBorderColor = (): CSSProperties['boxShadow'] => {
            // Base input theme does not have this extended functionality
            if ((isWarning || isSuccess || isUserWarning) && !isFocused) {
                return '';
            }

            // TODO discuss hover style with UX, this is in MUI but not 3p design
            return `inset 0 0 0 1px ${neutral600}`;
        };

        return {
            FormControl: {
                position: 'relative',
                w: '100%',
                h: '80px'
            },
            Select: {
                // Using box shadow so the change in border size for active does not
                // cause a reflow of the size of the input.
                border: 'none',
                boxShadow: inputBorderColor(),
                w: '100%',
                _hover: { boxShadow: inputHoverBorderColor() },
                _focus: {
                    zIndex: 1,
                    boxShadow: isInvalid ? `inset 0 0 0 1px ${error700}` : `inset 0 0 0 2px ${primary500}`
                },
                svg: {
                    width: '10px',
                    h: '5px'
                }
            },
            SelectIcon: {
                width: '10px',
                h: '5px'
            },
            FormLabel: {
                color: formLabelColor(),
                position: 'absolute',
                top: isLabelOnTop ? '2px' : '28px',
                px: 1,
                left: '11px',
                background: 'white !important',
                zIndex: 2,
                transform: 'translate(0, -50%)',
                transition: 'all 0.2s ease-in-out',
                fontSize: isLabelOnTop ? '12px' : '16px',
                fontStyle: 'normal',
                fontWeight: '400',
                letterSpacing: '0.15px',
                lineHeight: isLabelOnTop ? '12px' : '16px',
                _disabled: {
                    color: disabledColor0,
                    opacity: 1
                }
            },
            FormHelperText: {
                fontStyle: 'normal',
                fontWeight: '400',
                fontSize: '12px',
                letterSpacing: '0.4px',
                color: helperTextColor(),
                position: 'absolute',
                bottom: '4px',
                left: '16px'
            },
            FormErrorMessage: {
                fontStyle: 'normal',
                fontWeight: '400',
                fontSize: '12px',
                letterSpacing: '0.4px',
                color: isDisabled ? disabledColor0 : 'error.700',
                position: 'absolute',
                bottom: '4px',
                left: '16px'
            },
            PlaceHolderText: {
                fontStyle: 'normal',
                fontWeight: '400',
                fontSize: '12px',
                lineHeight: '20px',
                letterSpacing: '0.4px',
                color: helperTextColor(),
                marginLeft: '16px',
                my: '8px'
            }
        };
    }
};
